import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDefaultCurrency } from '../../hooks/useDefaultCurrency';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { Label } from '../../../components/ui/label';
import currenciesConfig from '../../../shared-config/currencies.config.json';

// Type for currency data from config
interface CurrencyConfigData {
  name: string;
  symbol: string;
  primaryCountry: string;
}

// Flag component using country-flag-icons CDN
function CurrencyFlag({ countryCode, size = 20 }: { countryCode: string; size?: number }) {
  // EU doesn't have a country flag, use a special EU flag
  const flagUrl = countryCode === 'EU'
    ? 'https://upload.wikimedia.org/wikipedia/commons/b/b7/Flag_of_Europe.svg'
    : `https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode}.svg`;

  return (
    <img
      src={flagUrl}
      alt={countryCode}
      width={size * 1.5}
      height={size}
      style={{ objectFit: 'cover', borderRadius: 2 }}
      loading="lazy"
    />
  );
}

export interface CurrencyOption {
  code: string;
  name: string;
  symbol: string;
  primaryCountry: string;
}

interface CurrencyPickerProps {
  value: string | null;
  onChange: (code: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  /** Show only these currencies (by code). If not provided, shows all. */
  allowedCurrencies?: string[];
  /** Compact mode: show only flag + code, no symbol (useful when symbol is shown elsewhere) */
  compact?: boolean;
  /**
   * When `true` and no value is set, auto-select the viewer's default currency
   * (user.preferred_currency > tenant.default_currency > 'USD'). The default is
   * re-applied if tenant settings resolve after first render, but never
   * overrides a value the user picked or one the parent supplied.
   */
  defaultToTenantCurrency?: boolean;
  className?: string;
  /** Inline style for the container */
  style?: React.CSSProperties;
}

// Build sorted currency options from config
const allCurrencyOptions: CurrencyOption[] = Object.entries(
  currenciesConfig.currencies as Record<string, CurrencyConfigData>
)
  .map(([code, data]) => ({
    code,
    name: data.name,
    symbol: data.symbol,
    primaryCountry: data.primaryCountry,
  }))
  .sort((a, b) => a.code.localeCompare(b.code));

// Map for quick lookup
const currencyMap = new Map(allCurrencyOptions.map((c) => [c.code, c]));

export function CurrencyPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  allowedCurrencies,
  compact = false,
  defaultToTenantCurrency = false,
  className,
  style,
}: CurrencyPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t('pickers.selectCurrency');
  const defaultCurrency = useDefaultCurrency();

  // Keep the latest onChange in a ref so the auto-fill effect doesn't re-run (and
  // potentially double-fire) when a caller passes an inline arrow as onChange.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Auto-fill the viewer's default currency when opted in. Tracking the value we
  // ourselves applied (vs. one the user picked) lets a late tenant-settings load
  // correct the initial 'USD' fallback without clobbering a real user choice.
  const userPickedRef = useRef(false);
  const appliedDefaultRef = useRef<string | null>(null);
  useEffect(() => {
    if (!defaultToTenantCurrency || userPickedRef.current) return;
    if (allowedCurrencies && !allowedCurrencies.includes(defaultCurrency)) return;
    if (value && value !== appliedDefaultRef.current) return; // parent-supplied value
    appliedDefaultRef.current = defaultCurrency;
    if (value !== defaultCurrency) onChangeRef.current(defaultCurrency);
  }, [defaultToTenantCurrency, value, defaultCurrency, allowedCurrencies]);

  const handleSelect = (code: string | null) => {
    userPickedRef.current = true;
    onChange(code);
  };

  const selectedOption = useMemo(
    () => (value ? currencyMap.get(value) || null : null),
    [value]
  );

  const currencyOptions = useMemo(() => {
    if (!allowedCurrencies) return allCurrencyOptions;
    return allCurrencyOptions.filter((c) => allowedCurrencies.includes(c.code));
  }, [allowedCurrencies]);

  const buttonContent = selectedOption ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex items-center gap-1.5">
          <CurrencyFlag countryCode={selectedOption.primaryCountry} size={16} />
          {compact ? (
            <span className="font-medium whitespace-nowrap">{selectedOption.code}</span>
          ) : (
            <>
              <span className="font-medium">{selectedOption.symbol}</span>
              <span className="truncate">{selectedOption.code}</span>
            </>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {selectedOption.name}
      </TooltipContent>
    </Tooltip>
  ) : (
    resolvedPlaceholder
  );

  return (
    <div className={cn(fullWidth && 'w-full', className)} style={style}>
      {label && (
        <Label className="mb-1.5 block">
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'justify-between font-normal',
              fullWidth && 'w-full',
              size === 'sm' && 'h-8 text-sm',
              !selectedOption && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {buttonContent}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchCurrency')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noCurrencyFound')}</CommandEmpty>
              <CommandGroup>
                {currencyOptions.map((option) => (
                  <CommandItem
                    key={option.code}
                    value={`${option.code} ${option.name} ${option.symbol}`}
                    onSelect={() => {
                      handleSelect(option.code);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'me-2 h-4 w-4',
                        value === option.code ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <CurrencyFlag countryCode={option.primaryCountry} size={16} />
                    <span className="ms-2 font-medium">{option.symbol}</span>
                    <span className="ms-1">{option.code}</span>
                    <span className="ms-auto text-muted-foreground text-xs truncate">
                      {option.name}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

// Export helper functions and data for external use
export function getCurrencyByCode(code: string): CurrencyOption | undefined {
  return currencyMap.get(code);
}

export function getCurrencySymbol(code: string): string {
  return currencyMap.get(code)?.symbol || code;
}

export function getCurrencyName(code: string): string {
  return currencyMap.get(code)?.name || code;
}

export function getAllCurrencies(): CurrencyOption[] {
  return allCurrencyOptions;
}

export { CurrencyFlag };
